import {
  Button, Card, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, InputAdornment,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography
} from '@material-ui/core';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useTheme } from '@material-ui/core/styles';
import { makeStyles } from '@material-ui/styles';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import AddIcon from '@material-ui/icons/Add';
import CloseIcon from '@material-ui/icons/Close';
import LocalOfferOutlinedIcon from '@material-ui/icons/LocalOfferOutlined';
import RefreshIcon from '@material-ui/icons/Refresh';
import RemoveIcon from '@material-ui/icons/Remove';
import SaveIcon from '@material-ui/icons/Save';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getUser, moneyFormat } from 'src/utils/Common';

const useStyles = makeStyles(theme => ({
  paper: { borderRadius: 12, overflow: 'hidden', boxShadow: '0 18px 55px rgba(20, 46, 63, .22)' },
  header: { padding: theme.spacing(1.75, 2.25), color: '#203746', background: 'linear-gradient(135deg, #f8fbfd 0%, #eef6fa 100%)', borderBottom: '1px solid #d9e5eb' },
  headerContent: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  headerTitle: { display: 'flex', alignItems: 'center', minWidth: 0 },
  titleText: { lineHeight: 1.2 },
  subtitle: { display: 'block', marginTop: 3, color: '#6b7f8b', fontSize: 12, fontWeight: 400 },
  headerIcon: {
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, width: 34, height: 34,
    marginRight: theme.spacing(1.25), color: '#245c7a', borderRadius: 6, backgroundColor: '#eaf3f8'
  },
  content: { overflowX: 'hidden', overflowY: 'auto', padding: theme.spacing(2.25), backgroundColor: '#fff', [theme.breakpoints.down('xs')]: { padding: theme.spacing(1) } },
  tableContainer: {
    border: '1px solid #d5e0e6', borderRadius: 9, overflowX: 'auto', boxShadow: '0 3px 12px rgba(31, 62, 80, .06)',
    WebkitOverflowScrolling: 'touch'
  },
  table: {
    width: '100%', tableLayout: 'fixed',
    '& th:nth-child(1)': { width: 58 },
    '& th:nth-child(2)': { width: 170 },
    '& th:nth-child(3)': { width: 110 },
    '& th:nth-child(4)': { width: 235 },
    '& th:nth-child(5)': { width: 145 },
    '& th:nth-child(6)': { width: 145 },
    [theme.breakpoints.down('xs')]: { minWidth: 760 }
  },
  headerCell: { padding: '11px 8px', color: '#fff', fontSize: 11, fontWeight: 700, letterSpacing: '.05px', whiteSpace: 'normal', overflowWrap: 'anywhere', backgroundColor: '#274d63' },
  bodyRow: { transition: 'background-color .15s ease', '&:hover td': { filter: 'brightness(.985)' } },
  itemCell: { paddingLeft: 8, paddingRight: 8, fontWeight: 700, color: '#28485b', backgroundColor: '#edf5f9' },
  descriptionCell: { minWidth: 0 },
  itemCode: { display: 'inline-block', padding: '2px 7px', marginBottom: 4, color: '#23617f', fontSize: 11, fontWeight: 700, letterSpacing: '.2px', backgroundColor: '#e7f2f8', border: '1px solid #cce2ee', borderRadius: 10 },
  itemDescription: { color: '#344955', fontSize: 13 },
  baseCell: { backgroundColor: '#f5f7f8' },
  editableCell: { minWidth: 0, backgroundColor: '#fff0d5', '& .MuiInputAdornment-root': { color: '#73838c', fontSize: 11 } },
  priceEditor: {
    display: 'flex', alignItems: 'stretch', justifyContent: 'flex-end', width: '100%', height: 42, marginLeft: 'auto',
    overflow: 'hidden', backgroundColor: '#fff', border: '1px solid #a9bfcc', borderRadius: 9,
    boxShadow: '0 2px 5px rgba(38, 72, 91, .08)', transition: 'border-color .15s, box-shadow .15s',
    '&:focus-within': { borderColor: '#27749a', boxShadow: '0 0 0 3px rgba(39, 116, 154, .13)' },
  },
  priceEditorError: { borderColor: `${theme.palette.error.main} !important`, boxShadow: '0 0 0 3px rgba(211, 47, 47, .10) !important' },
  priceField: {
    flex: 1, minWidth: 0, padding: '0 8px',
    '& .MuiInputBase-root': { height: '100%' },
    '& .MuiInputBase-input': { padding: 0, textAlign: 'right', fontWeight: 600, color: '#263f4d' },
    '& input[type=number]': { MozAppearance: 'textfield' },
    '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': { margin: 0, WebkitAppearance: 'none' }
  },
  stepButton: {
    width: 42, height: 42, padding: 0, color: '#236181', backgroundColor: '#f7fbfd', borderRadius: 0,
    '&:first-child': { borderRight: '1px solid #d3e0e7' },
    '&:last-child': { borderLeft: '1px solid #d3e0e7' },
    '&:hover': { color: '#fff', backgroundColor: '#27749a' },
    '&.Mui-disabled': { color: '#aebbc2', backgroundColor: '#f2f4f5' }
  },
  minimumError: { marginTop: 4, color: theme.palette.error.main, fontSize: 10, textAlign: 'right' },
  serviceCell: { backgroundColor: '#e9f5ec' },
  dawlanceCell: { backgroundColor: '#e7f0fb' },
  totalRow: { '& td': { paddingTop: 12, paddingBottom: 12, color: '#203746', fontWeight: 800, backgroundColor: '#e8eef2', borderTop: '2px solid #b9cbd5' } },
  totalAmount: { color: '#126b50 !important', fontSize: 15 },
  errorText: { marginTop: theme.spacing(1.25), padding: theme.spacing(1, 1.25), color: '#b42318', fontWeight: 500, backgroundColor: '#fff1f0', border: '1px solid #ffc9c5', borderRadius: 6 },
  actions: {
    padding: theme.spacing(1, 2.5), borderTop: '1px solid #dde5ea', backgroundColor: '#fff',
    [theme.breakpoints.down('xs')]: { padding: theme.spacing(1), '& button': { flex: 1 } }
  },
  applyButton: {
    minWidth: 170, backgroundColor: '#1d3748', '&:hover': { backgroundColor: '#122b3b' },
    [theme.breakpoints.down('xs')]: { minWidth: 0 }
  },
  resetButton: {
    marginTop: theme.spacing(1.5), borderColor: '#b8c7d1', color: '#36586d', backgroundColor: '#fff',
    [theme.breakpoints.down('xs')]: { width: '100%' }
  }
}));

const roundMoney = value => Math.round((Number(value) || 0) * 100) / 100;
const parseMoney = value => Math.max(0, parseFloat(value) || 0);

function ManualDiscountDialog({
  open, workOrderId, loading, items, dawlanceShares, serviceShopTypeId, discountRatio,
  setLabourTotal, setMaterialTotal, setTransportTotal, setExtraTotal, onDetailsChange,
  successMessage, errorMessage, onSuccessClose, onApply, onClose
}) {
  const classes = useStyles();
  const { t } = useTranslation('work_order');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
  const sessionUser = getUser();
  const hasServiceShopShare = Number(serviceShopTypeId) === 3;
  const isMaterialLocked = row => Number(sessionUser.TenantId) === 75 && Number(serviceShopTypeId) === 3 && row.typeId === 2;
  const [rows, setRows] = useState([]);
  const handleClose = successMessage ? onSuccessClose : onClose;
  const formatMoney = value => `${moneyFormat(value, sessionUser.TenantCode, sessionUser.CurrencyUnit)} ${sessionUser.CurrencyUnit}`;

  const buildRows = () => {
    const usedShareDetailIds = new Set();
    return (items || []).map(item => {
    const quantity = item.typeId === 2 ? Math.max(1, Number(item.quantity) || 1) : 1;
    const baseTotal = roundMoney(item.basePrice * quantity);
    const originalVatPrice = roundMoney(item.vatIncludedPrice * quantity);
    const apiShare = (dawlanceShares || []).find(share => {
      const shareDetailId = share.WorkOrderDetailId || share.workOrderDetailId;
      const shareMaterialId = share.MaterialId || share.materialId;
      if (!shareDetailId || usedShareDetailIds.has(Number(shareDetailId))) return false;
      return (item.workOrderDetailId && Number(shareDetailId) === Number(item.workOrderDetailId))
        || (item.materialId && Number(shareMaterialId) === Number(item.materialId));
    });
    const resolvedDetailId = apiShare?.WorkOrderDetailId || apiShare?.workOrderDetailId || item.workOrderDetailId;
    if (apiShare && resolvedDetailId) usedShareDetailIds.add(Number(resolvedDetailId));
    let dawlanceShare = 0;
    if (item.typeId === 1) dawlanceShare = Number(apiShare?.DawlanceShare ?? apiShare?.dawlanceShare) || 0;
    if (item.typeId === 2) dawlanceShare = originalVatPrice * 0.95;
    if (item.typeId === 4) dawlanceShare = originalVatPrice * 0.10;
    return {
      ...item,
      workOrderDetailId: resolvedDetailId,
      quantity,
      baseTotal,
      originalVatPrice,
      editedVatPrice: originalVatPrice.toFixed(2),
      dawlanceShare: roundMoney(dawlanceShare)
    };
    });
  };

  const publishRows = nextRows => {
    const totals = { 1: 0, 2: 0, 3: 0, 4: 0 };
    const details = nextRows.filter(row => row.workOrderDetailId).map(row => {
      const vatMultiplier = row.baseTotal > 0 ? row.originalVatPrice / row.baseTotal : 1;
      const newUnitPrice = isMaterialLocked(row) ? row.basePrice : vatMultiplier > 0 ? parseMoney(row.editedVatPrice) / row.quantity / vatMultiplier : 0;
      const roundedUnitPrice = Math.round(newUnitPrice * 10000) / 10000;
      totals[row.typeId] += roundedUnitPrice * row.quantity;
      return { WorkOrderDetailId: row.workOrderDetailId, NewUnitPrice: roundedUnitPrice };
    });
    setLabourTotal(roundMoney(totals[1]));
    setMaterialTotal(roundMoney(totals[2]));
    setTransportTotal(roundMoney(totals[3]));
    setExtraTotal(roundMoney(totals[4]));
    onDetailsChange(details);
  };

  useEffect(() => {
    if (!open) return;
    const nextRows = buildRows();
    setRows(nextRows);
    publishRows(nextRows);
  }, [open, items, dawlanceShares, serviceShopTypeId]);

  const changePrice = (index, value) => {
    const currentRow = rows[index];
    if (!currentRow || currentRow.originalVatPrice === 0 || isMaterialLocked(currentRow)) return;
    const numericValue = Number(value);
    const nextValue = value !== '' && numericValue > currentRow.originalVatPrice
      ? currentRow.originalVatPrice.toFixed(2)
      : value;
    const nextRows = rows.map((row, rowIndex) => rowIndex === index ? { ...row, editedVatPrice: nextValue } : row);
    setRows(nextRows);
    publishRows(nextRows);
  };

  const resetRows = () => {
    const nextRows = buildRows();
    setRows(nextRows);
    publishRows(nextRows);
  };

  // A branch (service-shop type 1) has no service-shop share. Dawlance owns the
  // complete discounted amount, so its share follows the edited price instead
  // of acting as a lower discount limit. Share limits only apply to type 3.
  const getDawlanceShare = row => hasServiceShopShare
    ? row.dawlanceShare
    : parseMoney(row.editedVatPrice);
  const getServiceShopShare = row => hasServiceShopShare
    ? parseMoney(row.editedVatPrice) - getDawlanceShare(row)
    : 0;
  const getMinimumPrice = row => hasServiceShopShare ? Math.max(0, roundMoney(getDawlanceShare(row))) : 0;
  const hasLimitError = rows.some(row => parseMoney(row.editedVatPrice) < getMinimumPrice(row));
  const hasMissingDetail = rows.some(row => !row.workOrderDetailId);
  const hasChanges = rows.some(row => Math.abs(parseMoney(row.editedVatPrice) - row.originalVatPrice) >= 0.005);
  const totalVatPrice = rows.reduce((total, row) => total + parseMoney(row.editedVatPrice), 0);

  return (
    <Dialog open={open} fullWidth fullScreen={isMobile} maxWidth="lg" onClose={() => !loading && handleClose()} PaperProps={{ className: classes.paper }}>
      <DialogTitle className={classes.header}>
        <div className={classes.headerContent}>
          <div className={classes.headerTitle}>
            <span className={classes.headerIcon}><LocalOfferOutlinedIcon fontSize="small" /></span>
            <Typography variant="subtitle1" className={classes.titleText}>
              <strong>{t('PriceAdjustmentTitle')} #{workOrderId}</strong>
              <span className={classes.subtitle}>{t('PriceAdjustmentSubtitle')}</span>
            </Typography>
          </div>
          <IconButton size="small" onClick={handleClose} disabled={loading}><CloseIcon /></IconButton>
        </div>
      </DialogTitle>
      {successMessage ? (
        <>
          <DialogContent className={classes.content}>
            <Card style={{ padding: 32, textAlign: 'center', color: '#2e8b57', boxShadow: 'none' }}>
              <CheckCircleOutlineIcon style={{ fontSize: 56, marginBottom: 12 }} />
              <Typography variant="h6">{successMessage}</Typography>
            </Card>
          </DialogContent>
          <DialogActions className={classes.actions}>
            <Button className={classes.applyButton} color="primary" variant="contained" onClick={onSuccessClose}>{t('Tamam')}</Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogContent className={classes.content}>
            <TableContainer className={classes.tableContainer}>
              <Table size="small" className={classes.table}>
                <TableHead><TableRow>
                  <TableCell className={classes.headerCell}>{t('Item')}</TableCell><TableCell className={classes.headerCell}>{t('Description')}</TableCell>
                  <TableCell className={classes.headerCell} align="right">{t('OriginalValue', { defaultValue: 'Original Value' })}</TableCell>
                  <TableCell className={classes.headerCell} align="right">{t('DiscountedValue', { defaultValue: 'Discounted Value' })}</TableCell>
                  <TableCell className={classes.headerCell} align="right">{t('ServiceShopShare')}</TableCell><TableCell className={classes.headerCell} align="right">{t('DawlanceShare')}</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {rows.map((row, index) => {
                    const editedPrice = parseMoney(row.editedVatPrice);
                    const minimumPrice = getMinimumPrice(row);
                    const invalid = editedPrice < minimumPrice;
                    const isPriceLocked = row.originalVatPrice === 0 || isMaterialLocked(row);
                    return <TableRow className={classes.bodyRow} key={`${row.workOrderDetailId}-${index}`}>
                      <TableCell className={classes.itemCell}>{row.itemLabel}</TableCell>
                      <TableCell className={classes.descriptionCell}>
                        {row.itemCode && <span className={classes.itemCode}>{row.itemCode}</span>}
                        <div className={classes.itemDescription}>{row.description}</div>
                      </TableCell>
                      <TableCell className={classes.baseCell} align="right">{formatMoney(row.originalVatPrice)}</TableCell>
                      <TableCell className={classes.editableCell} align="right">
                        <div>
                          <div className={`${classes.priceEditor} ${invalid ? classes.priceEditorError : ''}`}>
                            <IconButton className={classes.stepButton} aria-label={t('DecreasePrice')}
                              onClick={() => changePrice(index, Math.max(0, editedPrice - 1).toFixed(2))} disabled={isPriceLocked || editedPrice <= 0}>
                              <RemoveIcon fontSize="small" />
                            </IconButton>
                            <TextField className={classes.priceField}
                              type="number" value={row.editedVatPrice} onChange={event => changePrice(index, event.target.value)}
                              disabled={isPriceLocked}
                              inputProps={{ min: 0, max: row.originalVatPrice, step: 1 }}
                              InputProps={{ disableUnderline: true, endAdornment: <InputAdornment position="end">{sessionUser.CurrencyUnit}</InputAdornment> }}
                            />
                            <IconButton className={classes.stepButton} aria-label={t('IncreasePrice')}
                              onClick={() => changePrice(index, Math.min(row.originalVatPrice, editedPrice + 1).toFixed(2))}
                              disabled={isPriceLocked || editedPrice >= row.originalVatPrice}>
                              <AddIcon fontSize="small" />
                            </IconButton>
                          </div>
                          {invalid && <div className={classes.minimumError}>{t('Minimum')} {formatMoney(minimumPrice)}</div>}
                        </div>
                      </TableCell>
                      <TableCell className={classes.serviceCell} align="right">{formatMoney(getServiceShopShare(row))}</TableCell>
                      <TableCell className={classes.dawlanceCell} align="right">{formatMoney(getDawlanceShare(row))}</TableCell>
                    </TableRow>;
                  })}
                  <TableRow className={classes.totalRow}>
                    <TableCell /><TableCell />
                    <TableCell align="right">{t('TotalSum')}</TableCell>
                    <TableCell className={classes.totalAmount} align="right">{formatMoney(totalVatPrice)}</TableCell><TableCell />
                    <TableCell align="right">{t('DiscountRatio', { defaultValue: 'Discount Ratio' })}: {Number(discountRatio || 0).toFixed(2)}%</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            {hasLimitError && <Typography className={classes.errorText}>{t('ShareLimitError')}</Typography>}
            {hasMissingDetail && <Typography className={classes.errorText}>{t('DetailMatchError')}</Typography>}
            {errorMessage && <Typography className={classes.errorText}>{errorMessage}</Typography>}
            <Button className={classes.resetButton} variant="outlined" startIcon={<RefreshIcon />} onClick={resetRows}>{t('ResetOriginalValues')}</Button>
          </DialogContent>
          <DialogActions className={classes.actions}>
            <Button variant="outlined" onClick={handleClose} disabled={loading}>{t('Cancel')}</Button>
            <Button className={classes.applyButton} color="primary" variant="contained" startIcon={<SaveIcon />} onClick={onApply}
              disabled={loading || hasLimitError || hasMissingDetail || !hasChanges || rows.length === 0}>{loading ? t('Applying') : t('ApplyDiscount')}</Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}

export default ManualDiscountDialog;
